import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Button, Form, Nav, Spinner } from "react-bootstrap";
import { AppCheckbox } from "../components/form";
import axios from "axios";
import { Trash2, GripVertical, RotateCcw } from "lucide-react";
import SkuAutocomplete from "../components/SkuAutocomplete";
import { useDrag, useDrop } from "react-dnd";
import "./ShowroomChecklistPage.css";

const ITEM_TYPE = "checklist-item";

const WAREHOUSE_ID = { kharkiv: 1, kyiv: 3 };

const SHOWROOMS = [
    { key: "kharkiv", label: "Харків" },
    { key: "kyiv", label: "Київ" },
];

function api(showroom) {
    const base = `/api/showroom-checklist/${showroom}`;
    return {
        get: () => axios.get(base),
        addRail: (name) => axios.post(`${base}/rails`, { name }),
        renameRail: (railId, name) => axios.patch(`${base}/rails/${railId}`, { name }),
        deleteRail: (railId) => axios.delete(`${base}/rails/${railId}`),
        addItem: (railId, text, sku_id) => axios.post(`${base}/rails/${railId}/items`, { text, sku_id }),
        deleteItem: (railId, itemId) => axios.delete(`${base}/rails/${railId}/items/${itemId}`),
        reorderItems: (railId, itemIds) => axios.put(`${base}/rails/${railId}/items/reorder`, { itemIds }),
    };
}

function DraggableItem({ item, index, editMode, deleting, onMove, onDrop, onDelete, onToggle }) {
    const ref = useRef(null);
    const dragHandleRef = useRef(null);

    const [, drop] = useDrop({
        accept: ITEM_TYPE,
        hover(dragged) {
            if (dragged.index === index) return;
            onMove(dragged.index, index);
            dragged.index = index;
        },
    });

    const [{ isDragging }, drag, dragPreview] = useDrag({
        type: ITEM_TYPE,
        item: { index },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        end: () => onDrop(),
    });

    // drop target + drag preview — весь ряд
    // drag source — только иконка ⠿, чтобы кнопки удаления работали корректно
    dragPreview(drop(ref));
    drag(dragHandleRef);

    return (
        <div
            ref={ref}
            className="d-flex align-items-center gap-2 px-2 py-1 rounded"
            style={{
                background: item._checked ? "rgba(25, 135, 84, 0.1)" : "transparent",
                transition: "background 0.15s",
                cursor: editMode ? "default" : "pointer",
                opacity: isDragging ? 0.35 : 1,
            }}
            onClick={() => !editMode && onToggle(item._id)}
        >
            {editMode && (
                <span
                    ref={dragHandleRef}
                    style={{ color: "#adb5bd", flexShrink: 0, cursor: "grab", touchAction: "none" }}
                >
                    <GripVertical size={15} />
                </span>
            )}

            {!editMode && (
                <AppCheckbox
                    checked={!!item._checked}
                    onChange={() => onToggle(item._id)}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0 mb-0"
                />
            )}

            <span
                className={`flex-grow-1${item._checked ? " text-success" : ""}`}
                style={{
                    fontSize: "0.9rem",
                    fontWeight: item._checked ? 500 : "normal",
                }}
            >
                {item.text}
            </span>

            {editMode && (
                <Button
                    variant="link"
                    size="sm"
                    className="text-danger p-0 flex-shrink-0"
                    disabled={deleting}
                    onClick={(e) => { e.stopPropagation(); onDelete(item._id); }}
                >
                    {deleting
                        ? <Spinner animation="border" size="sm" style={{ width: 13, height: 13, borderWidth: 2 }} />
                        : <Trash2 size={13} />
                    }
                </Button>
            )}
        </div>
    );
}

function RailCard({ rail, showroom, skus, editMode, onRailUpdated, onRailDeleted }) {
    const [editingName, setEditingName] = useState(rail._new === true);
    const [nameVal, setNameVal] = useState(rail.name);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deletingItemId, setDeletingItemId] = useState(null);
    const nameRef = useRef(null);
    const railRef = useRef(rail);
    useEffect(() => { railRef.current = rail; }, [rail]);

    useEffect(() => {
        if (editingName && nameRef.current) {
            nameRef.current.focus();
            nameRef.current.select();
        }
    }, [editingName]);

    const commitName = async () => {
        const trimmed = nameVal.trim() || "Рейл";
        setEditingName(false);
        if (trimmed === rail.name) return;
        try {
            await api(showroom).renameRail(rail._id, trimmed);
            onRailUpdated({ ...rail, name: trimmed });
        } catch {
            setNameVal(rail.name);
        }
    };

    const handleDeleteRail = async () => {
        setSaving(true);
        try {
            await api(showroom).deleteRail(rail._id);
            onRailDeleted(rail._id);
        } finally {
            setSaving(false);
        }
    };

    const addItemFromSku = async (sku) => {
        const alreadyExists = rail.items.some((it) => it.sku_id != null && it.sku_id === sku.id);
        if (alreadyExists) return;
        const label = [sku.name, sku.type, sku.color, sku.size].filter(Boolean).join(" · ");
        try {
            const res = await api(showroom).addItem(rail._id, label, sku.id);
            onRailUpdated({ ...rail, items: [...rail.items, { ...res.data, _checked: false }] });
        } catch (e) {
            console.error("addItem error", e);
        }
    };

    const handleDeleteItem = async (itemId) => {
        setDeletingItemId(itemId);
        try {
            await api(showroom).deleteItem(rail._id, itemId);
            onRailUpdated({ ...rail, items: rail.items.filter((it) => it._id !== itemId) });
        } catch (e) {
            console.error("deleteItem error", e);
            alert(`Помилка видалення: ${e?.response?.data?.error || e.message}`);
        } finally {
            setDeletingItemId(null);
        }
    };

    const moveItem = useCallback((fromIndex, toIndex) => {
        const items = [...rail.items];
        const [moved] = items.splice(fromIndex, 1);
        items.splice(toIndex, 0, moved);
        onRailUpdated({ ...rail, items });
    }, [rail, onRailUpdated]);

    const saveOrder = useCallback(async () => {
        try {
            const current = railRef.current;
            await api(showroom).reorderItems(current._id, current.items.map((it) => it._id));
        } catch (e) {
            console.error("reorder error", e);
        }
    }, [showroom]);

    const toggleChecked = (id) => {
        onRailUpdated({
            ...rail,
            items: rail.items.map((it) =>
                it._id === id ? { ...it, _checked: !it._checked } : it
            ),
        });
    };

    const checkedCount = rail.items.filter((it) => it._checked).length;
    const total = rail.items.length;

    return (
        <div className="card checklist-card mb-3" style={{ boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
            <div className="card-header d-flex align-items-center gap-2 px-3 py-2">
                {editMode && (
                    <GripVertical size={16} className="text-muted flex-shrink-0" style={{ cursor: "grab" }} />
                )}

                {editMode && editingName ? (
                    <Form.Control
                        ref={nameRef}
                        size="sm"
                        placeholder="Назва рейлу…"
                        value={nameVal}
                        onChange={(e) => setNameVal(e.target.value)}
                        onBlur={commitName}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") commitName();
                            if (e.key === "Escape") {
                                setNameVal(rail.name);
                                setEditingName(false);
                            }
                        }}
                        style={{ fontWeight: 600, maxWidth: 260 }}
                    />
                ) : (
                    <span
                        className="fw-semibold flex-grow-1"
                        style={{ cursor: editMode ? "pointer" : "default" }}
                        onClick={() => editMode && setEditingName(true)}
                        title={editMode ? "Натисніть, щоб перейменувати" : undefined}
                    >
                        {rail.name || "Рейл"}
                    </span>
                )}

                {!editMode && total > 0 && (
                    <span className="badge bg-secondary ms-1" style={{ fontSize: "0.7rem" }}>
                        {checkedCount}/{total}
                    </span>
                )}

                {editMode && (
                    confirmDelete ? (
                        <div className="d-flex align-items-center gap-2 ms-auto">
                            <span className="text-danger small">Видалити?</span>
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={handleDeleteRail}
                                disabled={saving}
                            >
                                Так
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setConfirmDelete(false)}
                            >
                                Ні
                            </Button>
                        </div>
                    ) : (
                        <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0 ms-auto"
                            onClick={() => setConfirmDelete(true)}
                            title="Видалити рейл"
                        >
                            <Trash2 size={15} />
                        </Button>
                    )
                )}
            </div>

            <div className="card-body p-2">
                {rail.items.length === 0 && (
                    <div className="text-muted small px-1 py-1">Немає позицій</div>
                )}

                {rail.items.map((item, index) => (
                    <DraggableItem
                        key={item._id}
                        item={item}
                        index={index}
                        editMode={editMode}
                        deleting={deletingItemId === item._id}
                        onMove={moveItem}
                        onDrop={saveOrder}
                        onDelete={handleDeleteItem}
                        onToggle={toggleChecked}
                    />
                ))}

                {editMode && (
                    <div className="mt-2 px-1">
                        <SkuAutocomplete skus={skus} onSelect={addItemFromSku} />
                    </div>
                )}
            </div>
        </div>
    );
}

const EXCLUDED_USERS = new Set(["ritzlika@gmail.com", "annykameneva@gmail.com"]);

export default function ShowroomChecklistPage({ user }) {
    const [activeTab, setActiveTab] = useState("kharkiv");
    const [rails, setRails] = useState({ kharkiv: null, kyiv: null });
    const [loading, setLoading] = useState(false);
    const [skus, setSkus] = useState({ kharkiv: [], kyiv: [] });
    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        const warehouseId = WAREHOUSE_ID[activeTab];
        axios.get(`/api/products/skus?warehouseId=${warehouseId}`)
            .then((r) => setSkus((prev) => ({ ...prev, [activeTab]: r.data })))
            .catch(() => {});
    }, [activeTab]);

    const fetchRails = useCallback(async (showroom) => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/showroom-checklist/${showroom}`);
            const loaded = (res.data.rails || []).map((r) => ({
                ...r,
                items: r.items.map((it) => ({ ...it, _checked: false })),
            }));
            setRails((prev) => ({ ...prev, [showroom]: loaded }));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (rails[activeTab] === null) fetchRails(activeTab);
    }, [activeTab, rails, fetchRails]);

    const addRail = async () => {
        const idx = (rails[activeTab] || []).length + 1;
        const defaultName = `Рейл ${idx}`;
        try {
            const res = await api(activeTab).addRail(defaultName);
            const newRail = { ...res.data, items: res.data.items || [], _new: true };
            setRails((prev) => ({ ...prev, [activeTab]: [...(prev[activeTab] || []), newRail] }));
        } catch (e) {
            console.error("addRail error", e);
        }
    };

    const handleRailUpdated = (showroom, updated) => {
        setRails((prev) => ({
            ...prev,
            [showroom]: prev[showroom].map((r) => (r._id === updated._id ? updated : r)),
        }));
    };

    const handleRailDeleted = (showroom, railId) => {
        setRails((prev) => ({
            ...prev,
            [showroom]: prev[showroom].filter((r) => r._id !== railId),
        }));
    };

    const resetChecks = () => {
        setRails((prev) => ({
            ...prev,
            [activeTab]: (prev[activeTab] || []).map((r) => ({
                ...r,
                items: r.items.map((it) => ({ ...it, _checked: false })),
            })),
        }));
    };

    const currentRails = rails[activeTab] || [];
    const checkedTotal = currentRails.reduce((acc, r) => acc + r.items.filter((it) => it._checked).length, 0);
    const itemsTotal = currentRails.reduce((acc, r) => acc + r.items.length, 0);

    if (user && EXCLUDED_USERS.has(user.username)) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className={`container-fluid px-3 py-3${editMode ? " checklist-edit-mode" : ""}`} style={{ maxWidth: 720 }}>
            <h5 className="mb-3 fw-bold">Рейли шоурумів</h5>

            <Nav
                variant="tabs"
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3"
            >
                {SHOWROOMS.map(({ key, label }) => (
                    <Nav.Item key={key}>
                        <Nav.Link eventKey={key}>{label}</Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="secondary" />
                </div>
            ) : (
                <>
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <Button
                            variant={editMode ? "secondary" : "outline-secondary"}
                            onClick={() => setEditMode((v) => !v)}
                        >
                            {editMode ? "Готово" : "Редагувати"}
                        </Button>
                        {!editMode && itemsTotal > 0 && (
                            <>
                                <span className="text-muted ms-auto">
                                    {checkedTotal} / {itemsTotal}
                                </span>
                                <Button
                                    variant="outline-secondary"
                                    onClick={resetChecks}
                                    title="Скинути позначки"
                                    style={{ padding: "0.375rem 0.6rem" }}
                                >
                                    <RotateCcw size={16} />
                                </Button>
                            </>
                        )}
                    </div>

                    {currentRails.map((rail) => (
                        <RailCard
                            key={rail._id}
                            rail={rail}
                            showroom={activeTab}
                            skus={skus[activeTab] || []}
                            editMode={editMode}
                            onRailUpdated={(updated) => handleRailUpdated(activeTab, updated)}
                            onRailDeleted={(id) => handleRailDeleted(activeTab, id)}
                        />
                    ))}

                    {editMode && (
                        <Button
                            variant="outline-primary"
                            className="d-flex align-items-center gap-2 mt-2"
                            onClick={addRail}
                        >
                            + Додати рейл
                        </Button>
                    )}
                </>
            )}
        </div>
    );
}
